import { useCallback, useRef, useState } from 'react'
import { toast } from 'sonner'
import type { TEntry, TShow } from '@schemas'
import type { PendingIntakeEntry, useEntryIntakeManager } from './useEntryIntake'
import { PENDING_SYNC_DEFERRED_MESSAGE, syncPendingEntry } from './pending-entry-sync'
import { useCheckInEntry } from '@/api/hooks'
import { apiClient } from '@/lib/apiClient'
import { parseApiErrorPayload } from '@/lib/api-errors'

type EntryIntakeManager = ReturnType<typeof useEntryIntakeManager>

type CreateEntryMutation = {
  mutateAsync: (input: {
    showId: string
    submissionId?: string
    classId: string
    exhibitorId: string
    entryCode?: string
    fees: number
    notes?: string
    clubId?: string | null
    clubName?: string
  }) => Promise<TEntry>
}

type AllocateCodesMutation = {
  mutateAsync: (input: { showId: string; count: number }) => Promise<{ codes: string[] }>
}

type EntryCreatedOptions = {
  usedCode: string
  originalCode?: string | null
  className?: string | null
  classId?: string
  classCode?: string
}

type UsePendingEntrySyncOptions = {
  intake: EntryIntakeManager
  currentShowId?: string | null
  currentShowStatus?: TShow['status'] | null
  isOnline: boolean
  entryPermissionDisabled: boolean
  entryPermissionReason?: string
  allocationPermissionDisabled: boolean
  allocationPermissionReason?: string
  createEntry: CreateEntryMutation
  allocateCodes: AllocateCodesMutation
  onEntryCreated?: (entry: TEntry, options: EntryCreatedOptions) => void | Promise<void>
}

export function usePendingEntrySync({
  intake,
  currentShowId,
  currentShowStatus,
  isOnline,
  entryPermissionDisabled,
  entryPermissionReason,
  createEntry,
  onEntryCreated,
}: UsePendingEntrySyncOptions) {
  const checkInEntry = useCheckInEntry()
  const [isSyncing, setIsSyncing] = useState(false)
  const syncInFlightRef = useRef(false)

  const syncPendingEntries = useCallback(
    async (
      entries: PendingIntakeEntry[],
      options: { isBackground?: boolean } = {}
    ): Promise<{ success: boolean }> => {
      if (!currentShowId) return { success: false }
      if (!entries.length) return { success: true }

      const sanitizedEntries = entries.filter((entry): entry is PendingIntakeEntry =>
        Boolean(entry)
      )
      if (!sanitizedEntries.length) return { success: true }

      if (entryPermissionDisabled) {
        if (!options.isBackground) {
          toast.error(entryPermissionReason ?? 'Syncing entries requires additional permissions.')
        }
        return { success: false }
      }

      if (!isOnline) {
        if (!options.isBackground) {
          toast.error('Go online before syncing pending entries')
        }
        return { success: false }
      }

      if (syncInFlightRef.current) {
        return { success: false }
      }

      syncInFlightRef.current = true
      setIsSyncing(true)
      let hadErrors = false

      try {
        for (const requested of sanitizedEntries) {
          try {
            const result = await syncPendingEntry({
              intake,
              id: requested.id,
              showId: currentShowId,
              showStatus: currentShowStatus,
              create: input => createEntry.mutateAsync(input),
              checkIn: entryId => checkInEntry.mutateAsync({ entryId, payload: {} }),
              read: entryId => apiClient.getEntry(entryId),
            })
            if (!result) continue
            if (result === 'deferred') {
              hadErrors = true
              if (!options.isBackground) toast.info(PENDING_SYNC_DEFERRED_MESSAGE)
              continue
            }
            const { entry, pending, checkedIn, finished } = result
            await onEntryCreated?.(
              { ...entry, ...(checkedIn ? { status: 'checked_in' as const } : {}) },
              {
                usedCode: pending.code,
                originalCode: pending.rejectedCode ?? pending.code,
                className: checkedIn?.className ?? pending.className ?? null,
                classId: entry.classId ?? pending.classId,
                classCode: checkedIn?.classCode ?? undefined,
              }
            )
            if (!finished) hadErrors = true
          } catch (error) {
            hadErrors = true
            if (!options.isBackground) {
              toast.error(parseApiErrorPayload(error)?.message ?? 'Unable to sync pending entry')
            }
          }
        }

        return { success: !hadErrors }
      } catch (error) {
        toast.error(
          error instanceof Error
            ? `Unable to save sync progress: ${error.message}`
            : 'Unable to save sync progress on this device. Try again after checking browser storage.'
        )
        return { success: false }
      } finally {
        syncInFlightRef.current = false
        setIsSyncing(false)
      }
    },
    [
      createEntry,
      checkInEntry,
      currentShowId,
      currentShowStatus,
      entryPermissionDisabled,
      entryPermissionReason,
      intake,
      isOnline,
      onEntryCreated,
    ]
  )

  return { syncPendingEntries, isSyncing }
}
